import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import Select from "@/components/ui/Select";
import { CandidateService } from "@/services/candidateService";
import { JobService } from "@/services/jobService";
import { jobfitApplyConsentNote } from "../constants/consentModeNotes";

// Same attestation + consent chain the Suitable Jobs tab's per-job Apply
// button starts (see job-fit/component/AttestationDialog.jsx) — this is the
// job-agnostic entry point into it: pick any open job instead of one the
// AI job-fit model already scored. Everything downstream — the review
// route, staging, the consent email, confirmation — is unchanged and shared.
//
// A plain hand-rolled modal, not the shared AlertDialog primitives (contrast
// AttestationDialog, which uses them fine — it only has a checkbox). Radix's
// AlertDialog hardcodes `modal: true` with no override, which traps focus
// inside the dialog's own DOM subtree. This dialog's job picker (`Select`)
// portals its search input and option list straight to `document.body`, so
// the trap fought it: options were unclickable (`pointer-events: none` on
// `body`, now fixed in Select.jsx itself) and the search box could never
// keep focus long enough to type into (Radix's FocusScope yanks focus back
// on every keystroke). `ConvertToApplicantModal.jsx` already proves this
// exact `Select` works fine in a plain, non-Radix modal — same pattern here.
const ApplyToJobModal = ({ open, onClose, candidateId }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [jobs, setJobs] = useState([]);
  // {job_id: apply_state} for the jobs this candidate is already on. The
  // Suitable Jobs tab refuses the same two states per card; this picker sees
  // every open job, so it has to ask.
  const [applyStates, setApplyStates] = useState({});
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobId, setJobId] = useState("");
  const [checked, setChecked] = useState(false);
  const [consentText, setConsentText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setJobId("");
    setChecked(false);

    let cancelled = false;
    (async () => {
      setJobsLoading(true);
      try {
        // Every open job, not the first hundred: the picker searches this
        // list in the browser, so a job missing from it cannot be found at
        // all — and nothing on screen would say it was left out.
        const resp = await JobService.fetchAdminJobDetails({ pagination: false, status: "OPEN" });
        if (cancelled) return;
        setJobs(resp?.data?.jobs ?? resp?.jobs ?? []);
      } catch (err) {
        console.error("Failed to load open jobs:", err);
      } finally {
        if (!cancelled) setJobsLoading(false);
      }
    })();

    (async () => {
      try {
        const res = await CandidateService.fetchCandidateApplyStates(candidateId);
        if (cancelled) return;
        setApplyStates(
          Object.fromEntries(
            (res?.data?.results ?? []).map((row) => [String(row.job_id), row.apply_state]),
          ),
        );
      } catch (err) {
        // A failed read must not block applying: the server refuses a
        // duplicate either way, so the picker falls back to offering
        // everything rather than to offering nothing.
        console.error("Failed to load the candidate's apply states:", err);
        if (!cancelled) setApplyStates({});
      }
    })();

    if (!consentText) {
      (async () => {
        try {
          const res = await CandidateService.fetchJobFitApplyConsentText();
          if (!cancelled) setConsentText(res?.consent_text || "");
        } catch (err) {
          console.error("Failed to fetch job-fit apply consent text:", err);
          if (!cancelled) {
            setConsentText(
              "I confirm that I am authorized to submit this candidate's application to this job on their behalf.",
            );
          }
        }
      })();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const dismiss = () => {
    if (submitting) return;
    onClose();
  };

  // Which of the two modes the chosen job runs in. `fetchAdminJobDetails`
  // already resolves `require_jobfit_apply_email_consent` per job, so saying
  // it here costs no request; nothing is said until a job is picked, because
  // until then there is no answer to give.
  // One row per open job, with the two states that make a job unavailable
  // spelled out on the row itself. Left in the list rather than dropped: a
  // recruiter searching for a job they applied to yesterday needs to read why
  // it is not on offer, not fail to find it.
  const jobOptions = jobs.map((j) => {
    const state = applyStates[String(j.id)];
    if (state === "applied") {
      return { value: j.id, label: `${j.title} — already applied`, disabled: true };
    }
    if (state === "awaiting_candidate_consent") {
      return {
        value: j.id,
        label: `${j.title} — awaiting the candidate's confirmation`,
        disabled: true,
      };
    }
    return { value: j.id, label: j.title };
  });

  const selectedJob = jobs.find((j) => String(j.id) === String(jobId));
  const consentNote = jobfitApplyConsentNote(selectedJob?.require_jobfit_apply_email_consent);

  const handleContinue = () => {
    if (!jobId || !checked || submitting) return;
    const job = jobs.find((j) => String(j.id) === String(jobId));
    setSubmitting(true);
    onClose();
    navigate(`/admin/candidate-analysis/${candidateId}/apply-to-job/${jobId}`, {
      state: {
        attested: true,
        jobTitle: job?.title,
        fromPath: `${location.pathname}${location.search}`,
      },
    });
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={dismiss}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-xl border border-border-primary bg-background-secondary p-6 shadow-2xl">
        <div className="mb-1 flex items-start justify-between gap-3">
          <h2 className="text-base font-semibold text-text-primary">Apply to another job?</h2>
          <Button
            type="button"
            onClick={dismiss}
            disabled={submitting}
            aria-label="Close"
            className="shrink-0 rounded-md p-1 text-text-secondary transition-colors hover:bg-background-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={16} aria-hidden="true" />
          </Button>
        </div>
        <p className="mb-4 text-sm text-text-secondary">
          This reuses the candidate's already-confirmed information — no new resume parsing. You'll review and edit the
          details on the next screen before anything is submitted.
        </p>

        <div className="mb-4">
          <label className="mb-1.5 block text-sm font-medium text-text-secondary">Job</label>
          {jobsLoading ? (
            <p className="py-2 text-sm text-text-secondary">Loading open jobs…</p>
          ) : jobs.length === 0 ? (
            <p className="py-2 text-sm text-tone-amber">No open jobs found.</p>
          ) : (
            <Select value={jobId} onChange={setJobId} options={jobOptions} placeholder="Select a job" searchable />
          )}
        </div>

        <label className="mb-4 flex items-start gap-2 rounded-lg border border-border-primary bg-background-primary px-3 py-2.5 text-sm text-text-primary">
          <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} className="mt-0.5" />
          <span>{consentText || "Loading…"}</span>
        </label>

        {consentNote && <p className="mb-4 text-xs leading-snug text-text-secondary">{consentNote}</p>}

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            onClick={dismiss}
            disabled={submitting}
            className="rounded-md px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleContinue}
            disabled={!jobId || !checked || submitting}
            className="inline-flex h-9 items-center justify-center rounded-md bg-button-secondary px-4 text-sm font-medium text-text-base disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApplyToJobModal;
